import React, { useState, useEffect, useContext } from 'react';
import { getOrderCancellationReasons, getCancellationTypes, getFieldRepCancellationStatuses, mapFieldRepCancellationStatusOptions } from '../../services';
import { Autocomplete } from '../Autocomplete';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Button, CircularProgress, Grid, withStyles } from '@material-ui/core';
import { createOrderPDFReport, updateCancelReportDetails, orderManipulationRejectCancelledOrder } from '../../services';
import * as v from '../../helpers/validation';
import { DynamicFormModal } from '../DynamicForm';
import { orderRemarkTypes } from '../../enums';
import { OrderReviewContext } from './OrderReviewContext';
import CommunicationGrid from '../CommunicationGrid';

const styles = theme => ({
  costTable: {
    border: '1px solid #ccc',
    borderRadius: 10,
    background: '#eee',
    padding: 20,
  },

  tableInput: {
    margin: 10,
    padding: 10,
    border: 'none',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
  },
  fieldLabel: {
    margin: 0,
    marginBottom: 2,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.2,
    color: '#777',
    textAlign: 'left',
    width: '100%',
  },
});

const cancelReportSelectStyleOptions = {
  control: base => ({
    ...base,
    minHeight: '31px',
    height: '31px',
    fontSize: '14px',
    borderRadius: '4px',
    borderColor: '#aaa',
  }),
  valueContainer: base => ({
    ...base,
    padding: '2px 8px',
    height: '31px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: base => ({
    ...base,
    height: '31px',
  }),
};

const getOrderCancellationTypeId = order =>
  order?.canceltype ?? order?.cancelType ?? order?.CancelType ?? order?.cancellationTypeId ?? order?.CancellationTypeId;

const getOrderFieldCancelStatusLookupId = order =>
  order?.fieldCancelStatusLookupId ?? order?.FieldCancelStatusLookupId;

const FIELD_REP_DID_NOT_CANCEL_OPTION = {
  value: null,
  label: 'Field Rep did not cancel',
};

const buildFieldRepCancellationStatusOptions = statuses => [
  FIELD_REP_DID_NOT_CANCEL_OPTION,
  ...mapFieldRepCancellationStatusOptions(statuses),
];

const getSelectedFieldRepCancellationStatusOption = (statuses, order) => {
  const options = buildFieldRepCancellationStatusOptions(statuses);
  const fieldCancelStatusId = getOrderFieldCancelStatusLookupId(order);

  if (fieldCancelStatusId == null) {
    return FIELD_REP_DID_NOT_CANCEL_OPTION;
  }

  return options.find(status => status.value == fieldCancelStatusId) ?? FIELD_REP_DID_NOT_CANCEL_OPTION;
};

const buildUpdateCancelReportDetailsPayload = orderDetails => {
  const {
    canceltype,
    cancelType,
    CancelType,
    cancellationTypeId,
    CancellationTypeId,
    fieldCancelStatusLookupId,
    FieldCancelStatusLookupId,
    ...rest
  } = orderDetails;

  return {
    ...rest,
    cancellationTypeId: getOrderCancellationTypeId(orderDetails),
    fieldCancelStatusLookupId: getOrderFieldCancelStatusLookupId(orderDetails),
  };
};

const orderActionInputs = [
  {
    inputType: 'text',
    field: 'orderLineId',
    label: 'Orderline ID',
    validate: [v.required()],
    disabled: true,
  },
  {
    inputType: 'text',
    field: 'reason',
    label: 'Reason',
    validate: [v.required()],
  },
];

export const OrderCancelReport = withStyles(styles)(props => {
  // const { state } = useContext(OrderReviewContext);
  const {
    order: orderInfo,
    setOrder,
    classes,
    setPdfPreviewLink,
    setIsCompleteDialogOpen,
    // setCancelledOrder,
  } = props;
  const [cancellationReasonOptions, setCancellationReasonOptions] = useState([]);
  const [selectedCancellationReasonOption, setSelectedCancellationReasonOption] = useState(null);
  const [cancellationTypeOptions, setCancellationTypeOptions] = useState([]);
  const [selectedCancellationTypeOption, setSelectedCancellationTypeOption] = useState(null);
  const [fieldRepCancellationStatusOptions, setFieldRepCancellationStatusOptions] = useState([]);
  const [selectedFieldRepCancellationStatusOption, setSelectedFieldRepCancellationStatusOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreviewLink, setIsLoadingPreviewLink] = useState(false);
  const [isReturningToFieldRep, setIsReturningToFieldRep] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [initData] = useState({
    orderLineId: orderInfo.orderTicket,
    reason: '',
  });

  const [cancelledOrderRemarks, setCancelledOrderRemarks] = useState([]);
  const [outputOrderRemark, setOutputOrderRemark] = useState({});
  const [outputRemark, setOutputRemark] = useState({});
  const [shouldUpdateCancelledOrder, setShouldUpdateCancelledOrder] = useState(false);

  const {
    state: { formHeaders, ausumOrLc360Links, isAusumOrLc360, getOrderInfo },
  } = useContext(OrderReviewContext);

  const getPdfPreview = async () => {
    setIsLoadingPreviewLink(true);
    try {
      // replace the output order remark with the local version saved in state
      const updatedOrderDetails = Object.assign({}, orderInfo, {
        orderRemarks: orderInfo.orderRemarks.map(or =>
          or.orderRemarksId === outputRemark.orderRemarksId ? outputRemark : or,
        ),
      });

      await updateCancelReportDetails(buildUpdateCancelReportDetailsPayload(updatedOrderDetails));
      const link = await createOrderPDFReport(orderInfo.id);
      await getOrderInfo?.();
      setIsCompleteDialogOpen(true);
      setPdfPreviewLink(link);
    } catch (err) {
      console.log(err);
    }
    setIsLoadingPreviewLink(false);
  };

  const handleReturnOrderToFieldRep = async formData => {
    setIsReturningToFieldRep(true);
    try {
      await orderManipulationRejectCancelledOrder(formData);
    } catch (err) {
      console.log(err);
    }
    setIsReturningToFieldRep(false);
  };

  // update order info object
  const updateOrderInfo = async (updates = {}) => {
    const updatedOrderInfo = Object.assign({}, orderInfo, updates);
    setOrder(updatedOrderInfo);
  };

  // update cancellation reason
  useEffect(() => {
    if (!selectedCancellationReasonOption) {
      return;
    }

    updateOrderInfo({
      cancellationReasonId: selectedCancellationReasonOption.value,
    });
  }, [selectedCancellationReasonOption]);

  // update cancellation type
  useEffect(() => {
    if (!selectedCancellationTypeOption) {
      return;
    }

    updateOrderInfo({
      canceltype: selectedCancellationTypeOption.value,
    });
  }, [selectedCancellationTypeOption]);

  // update field rep cancellation status
  useEffect(() => {
    if (!selectedFieldRepCancellationStatusOption) {
      return;
    }

    updateOrderInfo({
      fieldCancelStatusLookupId: selectedFieldRepCancellationStatusOption.value,
    });
  }, [selectedFieldRepCancellationStatusOption]);

  useEffect(() => {
    // filter all the CANCELLED order remarks and the one final OUTPUT remark
    const cancelled = orderInfo.orderRemarks.filter(
      orderRemark => orderRemark.orderRemarkTypeId === orderRemarkTypes.CANCEL,
    );
    const output = orderInfo.orderRemarks.find(
      orderRemark => orderRemark.orderRemarkTypeId === orderRemarkTypes.OUTPUT,
    );

    // if there is no OUTPUT remark, generate one and add it to the array
    if (!output) {
      const outputRemarkTemplate = {
        orderRemarkTypeId: orderRemarkTypes.OUTPUT,
        remark: '',
      };
      updateOrderInfo({
        orderRemarks: orderInfo.orderRemarks.concat(outputRemarkTemplate),
      });
    } else {
      // set in local state for easy updating
      setCancelledOrderRemarks(cancelled);

      // set this now, update it locally, and update it to the parent before saving
      // since we are filtering out the single output remark and using it in a text box (being prop drilled from parent), the cursor in the text box moves to the end on each re-render... so you must track it locally
      setOutputRemark(output);
    }
  }, [orderInfo.orderRemarks]);

  useEffect(() => {
    const loadCancellationOptionsAsync = async () => {
      try {
        const [reasons, types, fieldRepStatuses] = await Promise.all([
          getOrderCancellationReasons(),
          getCancellationTypes(),
          getFieldRepCancellationStatuses(),
        ]);

        const mappedReasons = reasons.map(r => ({
          value: r.cancellationReasonId ?? r.CancellationReasonId,
          label: r.name ?? r.Name,
        }));
        setCancellationReasonOptions(mappedReasons);
        setSelectedCancellationReasonOption(
          mappedReasons.find(r => r.value === (orderInfo.cancellationReasonId ?? orderInfo.CancellationReasonId)),
        );

        const mappedTypes = types.map(t => ({
          value: t.cancellationTypeId ?? t.CancellationTypeId,
          label: t.name ?? t.Name,
        }));
        setCancellationTypeOptions(mappedTypes);
        setSelectedCancellationTypeOption(
          mappedTypes.find(t => t.value === getOrderCancellationTypeId(orderInfo)),
        );

        const mappedFieldRepStatuses = buildFieldRepCancellationStatusOptions(fieldRepStatuses);
        setFieldRepCancellationStatusOptions(mappedFieldRepStatuses);
        setSelectedFieldRepCancellationStatusOption(
          getSelectedFieldRepCancellationStatusOption(fieldRepStatuses, orderInfo),
        );
      } catch (error) {
        console.log(error);
      }
    };

    loadCancellationOptionsAsync();
  }, []);

  return (
    <div className={cn('col-start-center', 'flex-1')} style={{ paddingBottom: 50 }}>
      <div style={{ width: '80%' }}>
        <div className={cn('align-stretch', 'row-between-center')} style={{ marginBottom: 10 }}>
          <h2>Cancellation Report</h2>
          <Button
            disabled={isSubmitting || isReturningToFieldRep}
            style={{
              marginLeft: 10,
              backgroundColor: 'var(--jmi-blue)',
              color: '#fff',
            }}
            variant="contained"
            // onClick={handleSave}
            onClick={() => setModalVisible(true)}
          >
            {isSubmitting || isLoadingPreviewLink ? (
              <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} />
            ) : (
              'Return to Field Rep'
            )}
          </Button>
        </div>

        {isAusumOrLc360 && (
          <div className={cn('align-stretch', 'row-start-center')} style={{ marginBottom: 10 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Links: </h3>

            {/* <div className={cn('row-start-center')}> */}
            {ausumOrLc360Links &&
              ausumOrLc360Links.map(({ formDefinitionId, answers, questionValue }) => (
                <h4 key={formDefinitionId} className={cn('row-start-center')}>
                  <a
                    className={cn('account-dropdown-link')}
                    target="_blank"
                    href={answers.length !== 0 && answers[0].text !== null ? answers[0].text : ''}
                  >
                    {questionValue}
                  </a>
                </h4>
              ))}
            {/* </div> */}
          </div>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <div className={classes.formField}>
              <p className={classes.fieldLabel}>Cancel Reason</p>
              <Autocomplete
                options={cancellationReasonOptions}
                value={selectedCancellationReasonOption}
                onChange={setSelectedCancellationReasonOption}
                placeholder={'Please select a cancellation reason'}
                styleOptions={cancelReportSelectStyleOptions}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <div className={classes.formField}>
              <p className={classes.fieldLabel}>Field Rep Cancel Reason</p>
              <Autocomplete
                options={fieldRepCancellationStatusOptions}
                value={selectedFieldRepCancellationStatusOption}
                onChange={setSelectedFieldRepCancellationStatusOption}
                placeholder={'Please select a field rep cancellation reason'}
                styleOptions={cancelReportSelectStyleOptions}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <div className={classes.formField}>
              <p className={classes.fieldLabel}>Cancellation Type</p>
              <Autocomplete
                options={cancellationTypeOptions}
                value={selectedCancellationTypeOption}
                onChange={setSelectedCancellationTypeOption}
                placeholder={'Please select a cancellation type'}
                styleOptions={cancelReportSelectStyleOptions}
              />
            </div>
          </Grid>
        </Grid>

        <div className={cn('mr-20')} style={{ marginTop: 35, marginBottom: 10 }}>
          Order Remarks:
        </div>
        {cancelledOrderRemarks.map(orderRemark => (
          <div className={cn('row-between-start')}>
            <div className={cn('flex-1', 'row-center-center')}>
              <textarea
                disabled
                className={cn('flex-1')}
                value={orderRemark.remark}
                style={{ fontFamily: 'PT Sans', fontSize: 14 }}
              />
            </div>
          </div>
        ))}

        {!cancelledOrderRemarks.length && (
          <div className={cn('row-between-start')}>
            <div className={cn('flex-1', 'row-center-center')} style={{ color: '#777' }}>
              There are no remarks on this order.
            </div>
          </div>
        )}

        <div className={cn('mr-20')} style={{ marginTop: 35, marginBottom: 10 }}>
          Final Output Remark:
        </div>
        <div className={cn('row-between-start')}>
          <div className={cn('flex-1', 'row-center-center')}>
            <textarea
              className={cn('flex-1')}
              // value={outputOrderRemark.remark}
              value={outputRemark.remark}
              onChange={evt =>
                setOutputRemark({
                  ...outputRemark,
                  remark: evt.target.value,
                })
              }
              rows={10}
              style={{ fontFamily: 'PT Sans', fontSize: 14 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', height: 400, marginTop: 30 }}>
          <CommunicationGrid
            gridName="QA_ORDER_CANCEL_COMMUNICATION"
            orderLineId={initData.orderLineId}
            editable={true}
          />
        </div>

        {/* cost table */}
        <div className={cn('row-center-center', 'flex-1')} style={{ marginTop: 30 }}>
          <Button
            disabled={isSubmitting || isLoadingPreviewLink || !selectedCancellationTypeOption}
            style={{
              marginLeft: 10,
              backgroundColor: '#17c80d',
              color: '#fff',
              opacity: isSubmitting || isLoadingPreviewLink || !selectedCancellationTypeOption ? 0.5 : 1,
            }}
            variant="contained"
            onClick={getPdfPreview}
          >
            {isSubmitting || isLoadingPreviewLink ? (
              <CircularProgress style={{ fill: '#fff', color: '#fff' }} size={25} />
            ) : (
              'Generate Report'
            )}
          </Button>
        </div>
      </div>

      <DynamicFormModal
        inputs={orderActionInputs}
        mode={'EDIT'}
        data={initData}
        onSave={handleReturnOrderToFieldRep}
        modalVisible={modalVisible}
        hideModal={() => setModalVisible(false)}
        modalLabel={'Return Order to Field Rep'}
        redirectOnSuccess={'/orders/qa'}
      />
    </div>
  );
});

OrderCancelReport.propTypes = {
  order: PropTypes.object.isRequired,
};
